/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import MultiFilePreviewModal from '../../../components/MultiFilePreviewModal'
import LoadingSpinner from '../../../components/LoadingSpinner'
import FileMerge from './FileMerge'
import { Popover, OverlayTrigger } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import useChecksumService from '../../../hooks/useChecksumService'
import SortableListWithDragSupport from './SortableListWithDragSupport'
import type { FileWithMetadata, DocumentMigrationFormData } from './types'

interface FileDropZoneProps {
  isSiteUpload?: boolean
  isUploadAndFile?: boolean
  selectedFiles: FileWithMetadata[]
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileWithMetadata[]>>
  values?: DocumentMigrationFormData
}

const MOBILE_SCREEN_SIZE = 768
// Document Migration file size limits
const MAX_FILE_SIZE = 2.5 * 1024 * 1024 * 1024 // 2.5GB per file
const MAX_FILES = 50 // Maximum 50 files at once

function FileDropZone({ isSiteUpload, isUploadAndFile, selectedFiles, setSelectedFiles }: FileDropZoneProps) {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { sha1_hash, getFileSizeLimit } = useChecksumService()
  const [inputKey, setInputKey] = useState(Date.now())
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_SCREEN_SIZE)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const isDuplicate = (newFile: File, existingFiles: FileWithMetadata[]): boolean => {
    return existingFiles.some((file) => file.name === newFile.name && file.size === newFile.size)
  }

  const fileAddedHandler = async (filesFromUpload: File[]): Promise<FileWithMetadata[]> => {
    // Add relativePath to files
    const files: FileWithMetadata[] = filesFromUpload.map((file, index) => {
      const webkitFile = file as any // Type assertion for webkitRelativePath
      const relativePath = webkitFile.webkitRelativePath || file.name

      return {
        id: `${Date.now()}-${index}-${file.name}`, // Generate unique ID for drag-and-drop
        name: file.name,
        size: file.size,
        file,
        relativePath,
        extension: file.name.split('.').pop()?.toLowerCase()
      }
    })

    try {
      // Use 2.5GB file size limit for document migration
      const dataFileSizeLimit = MAX_FILE_SIZE

      // Calculate checksums and validate
      const checksumData = await sha1_hash(filesFromUpload, dataFileSizeLimit)

      // Map checksum data back to files
      checksumData.forEach((itemChecksum) => {
        files.forEach((addedFile) => {
          if (addedFile.name === itemChecksum.fileName) {
            if ('checksumValidate' in itemChecksum) {
              // Validation failed - only set if it's a string error message
              if (typeof itemChecksum.checksumValidate === 'string') {
                addedFile.checksumValidate = itemChecksum.checksumValidate
              }
            } else {
              // Validation succeeded
              addedFile.checksum = itemChecksum
            }
          }
        })
      })
    } catch (error) {
      console.error('Error processing files:', error)
    }

    return files
  }

  const processFiles = async (fileList: FileList | null) => {
    if (!fileList) return

    const addedFiles = Array.from(fileList)
    const uniqueNewFiles = addedFiles.filter((file) => !isDuplicate(file, selectedFiles))

    if (uniqueNewFiles.length > 0) {
      // Check maximum files limit
      const totalFiles = selectedFiles.length + uniqueNewFiles.length
      if (totalFiles > MAX_FILES) {
        alert(
          `Maximum ${MAX_FILES} files allowed. You are trying to add ${uniqueNewFiles.length} files, but you already have ${selectedFiles.length} selected.`
        )
        return
      }

      setIsProcessing(true)
      try {
        const filesWithMetadata = await fileAddedHandler(uniqueNewFiles)
        const updatedFiles = [...selectedFiles, ...filesWithMetadata]
        setSelectedFiles(updatedFiles)
      } finally {
        setIsProcessing(false)
      }
    }
  }

  const removeFile = (indexToRemove: number) => {
    setSelectedFiles(selectedFiles.filter((_, index) => index !== indexToRemove))
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesFromInput = e.target.files
    if (filesFromInput && filesFromInput.length > 0) {
      await processFiles(filesFromInput)
      setInputKey(Date.now()) // Reset input
    }
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFiles(e.dataTransfer.files)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewFiles, setPreviewFiles] = useState<any[]>([])

  const preview = (file: FileWithMetadata) => {
    if (!file.checksum?.existFiles || file.checksum.existFiles.length === 0) {
      return
    }

    // Show preview of existing files with same checksum
    const outputFiles = file.checksum.existFiles.reduce((existFiles: any[], fileItem: any) => {
      const filePath = existFiles.find((f) => f.fileFullPath === fileItem.fileFullPath)
      if (!filePath) {
        existFiles.push(fileItem)
      }
      return existFiles
    }, [])

    setPreviewFiles(outputFiles)
    setPreviewModalOpen(true)
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_SCREEN_SIZE)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const popoverMixedValues = (
    <Popover
      id="multiple-files-popup-info"
      style={{
        zIndex: 9999,
        position: 'absolute',
        display: 'block',
        opacity: 1,
        visibility: 'visible'
      }}
    >
      <Popover.Body>
        <div dangerouslySetInnerHTML={{ __html: t('DOCS_MIGRATION_TOOLTIP_TXT') }}></div>
      </Popover.Body>
    </Popover>
  )

  return (
    <>
      <div
        className={`drop-zone drop with-popover dropClass ${isDragOver ? 'is-dragover' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {(!isMobile || !isSiteUpload) && (
          <>
            {isUploadAndFile ? (
              <>
                <span>{t('DOC_MIGRATION_DND_TXT')}</span>
                <div dangerouslySetInnerHTML={{ __html: t('BROWSE_DOC_AND_FOLDER_WITH_LABEL') }} />
                <input
                  key={inputKey}
                  onChange={handleFileChange}
                  type="file"
                  name="files"
                  id="browse-documents"
                  multiple
                  className="visibility-h pos-absolute"
                />
                <input
                  key={`${inputKey}-folder`}
                  onChange={handleFileChange}
                  type="file"
                  id="folders"
                  multiple
                  // @ts-expect-error - webkitdirectory is non-standard but required for folder selection
                  webkitdirectory="webkitdirectory"
                  className="visibility-h pos-absolute"
                />
                <i className="cloud-mig-upload-icon icon-big"></i>
                <p className="mb14">{t('UPLOAD_AND_FILE_DROP_ZONE_TEXT_INSTRUCTION')}</p>
                {selectedFiles?.length ? <h4>{t('UPLOAD_AND_FILE_DROP_ZONE_SELECTED_DOC_TITLE')}</h4> : null}
              </>
            ) : (
              <>
                <span>{t('DOC_MIGRATION_DND_TXT')}</span>
                <OverlayTrigger trigger={['hover', 'focus']} placement="left" overlay={popoverMixedValues}>
                  <button className="btn-show-tooltip" type="button"></button>
                </OverlayTrigger>
                <i className="cloud-mig-upload-icon"></i>
                <div dangerouslySetInnerHTML={{ __html: t('BROWSE_DOC_WITH_LABEL') }} />
                <input
                  key={inputKey}
                  onChange={handleFileChange}
                  type="file"
                  name="files"
                  id="browse-documents"
                  multiple
                  className="visibility-h pos-absolute"
                />
              </>
            )}
          </>
        )}

        {isMobile && isSiteUpload && (
          <>
            <div dangerouslySetInnerHTML={{ __html: t('CLICK_TO_BROWSE_ONLY_DOC') }} />
            <input
              key={inputKey}
              onChange={handleFileChange}
              type="file"
              name="files"
              id="browse-documents"
              multiple
              className="visibility-h pos-absolute"
            />
            <i className="cloud-mig-upload-icon"></i>
          </>
        )}

        {!isSiteUpload && !isUploadAndFile && (
          <span>
            {t('PREP_OR')}{' '}
            <label htmlFor="folders" className="btn-upload-browse font-weight-normal">
              {t('DOC_MIGRATION_BROWSE_FOLDER')}
            </label>
            <input
              key={inputKey}
              onChange={handleFileChange}
              type="file"
              id="folders"
              multiple
              // @ts-expect-error - webkitdirectory is non-standard but required for folder selection
              webkitdirectory="webkitdirectory"
              className="visibility-h pos-absolute"
            />
          </span>
        )}

        {isProcessing && (
          <div className="text-center p-3">
            <LoadingSpinner size={30} centered />
          </div>
        )}

        <div className="transfers-wrapper" id="files">
          <SortableListWithDragSupport
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            removeFile={removeFile}
            preview={preview}
            isUploadAndFile={isUploadAndFile}
          />
        </div>

        {/* FileMerge component for mobile site uploads */}
        {isSiteUpload && isMobile && <FileMerge files={selectedFiles} values={undefined} />}
      </div>
      {previewModalOpen && previewFiles.length > 0 && (
        <MultiFilePreviewModal fileId={previewFiles} onClose={() => setPreviewModalOpen(false)} />
      )}
    </>
  )
}

export default FileDropZone
